import type { ReactNode } from "react";

/**
 * The block a section of a module page sits in.
 *
 * ==========================================================================================
 * PROMOTED ON THE FOURTH CONSUMER, EXACTLY AS THE THIRD ONE ASKED
 * ==========================================================================================
 *
 * `rounded-control border border-line bg-panel p-5` was expenses' treatment, repeated inline by
 * income, wrapped in a `Panel` by investments, and copied into a `Panel` again by properties. That
 * fourth copy left instructions:
 *
 *     "**A fourth copy should promote this to `@gladis/ui` and delete the other three**, and
 *     whoever needs one should read this paragraph as permission rather than repeat it."
 *
 * Vehicles is the fourth. This is that promotion, and the two domain copies are gone rather than
 * left exported — a component nobody imports is an invitation, which is the standing argument in
 * this package's index for removing a retired mark instead of keeping it around.
 *
 * ==========================================================================================
 * WHAT IT IS NOT
 * ==========================================================================================
 *
 * It is not `SectionCard`, which lives in `apps/app` and carries a headline FIGURE above a chart —
 * `StatCard`'s surface for content that is not a row of numbers. That component answers "a figure
 * and its picture"; this one answers "a heading and some content", and collapsing them would give
 * every module section an optional value slot it has no figure for.
 *
 * It is not a `StatCard` either, for the reason `SectionCard` gives: "everything in a stat card must
 * be a number or a name, because the moment one starts holding a sentence the row stops being
 * scannable."
 */
export interface PanelProps {
  /** Names the SUBJECT of the block — what these rows are about, not the window they cover. */
  title: string;
  description?: string | undefined;
  /** Sits at the end of the header row — a count, a toggle, a control belonging to this block. */
  aside?: ReactNode | undefined;
  children: ReactNode;
}

export function Panel({ title, description, aside, children }: PanelProps): ReactNode {
  return (
    <section className="rounded-control border-line bg-panel border p-5">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          {description !== undefined && <p className="text-muted mt-1 text-sm">{description}</p>}
        </div>
        {aside}
      </header>
      {children}
    </section>
  );
}
